import { reelSectionWords } from '@src/services/reel/reelSectionWords';
import { useMemo } from 'react';

import { sectionSearchMatches } from './reelEditorSearch';

import type { ReelSearchMatch } from './reelEditorSearch';
import type { ReelEditorRow } from './useReelEditor';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';

const NO_MATCHES: ReelSearchMatch[] = [];

/**
 * PLAT-11074 — where in the reel the query was found, in playback order.
 *
 * Given the rows the search left on screen rather than the whole reel, so the
 * places Enter walks through are the places the creator can see — and so the
 * Sections a search filtered out are not walked into.
 *
 * The words are read off the timeline per Section, the way
 * {@link useSectionWords} does for the scrub strip: a reel's transcript runs to
 * thousands of tokens and nothing keeps a copy of them. Only a live query walks
 * them at all.
 */
export function useReelSearchMatches(
  editorStore: ClipPreviewEditorStore | null,
  rows: readonly ReelEditorRow[],
  query: string,
): ReelSearchMatch[] {
  // Read the observable *before* the memo so MobX records the dependency — an
  // edit in the Transcript tab re-runs the search rather than leaving it
  // pointing at words that are gone.
  const transcriptElements = editorStore?.transcriptElements;

  return useMemo(() => {
    if (!editorStore || !query) return NO_MATCHES;
    const { timeline } = editorStore.editor;

    return rows.flatMap(row => {
      const found = sectionSearchMatches(
        row.id,
        reelSectionWords(timeline, row.elementIds),
        query,
      );
      // A Section on screen for its *name* — or for its chapter's — has nothing
      // said inside it that matched, but it is still one of the places the
      // search found: Enter walks to its start rather than stepping over it.
      return found.length > 0
        ? found
        : [{ sectionId: row.id, time: row.startTime }];
    });
    // `transcriptElements` is the change signal rather than an input — the
    // words are read off the timeline.
  }, [editorStore, rows, query, transcriptElements]);
}
